import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ExternalLink, MapPin, ArrowRight, Loader2, Quote, CalendarClock, Sparkles, Landmark } from "lucide-react";
import { getPerson, getPersonArticle } from "@/lib/api";
import { T, useLanguage } from "@/context/LanguageContext";
import { useTTS } from "@/context/TTSContext";
import { InlineListen } from "@/components/site/ListenControl";
import { Seo } from "@/components/site/Seo";
import { canonicalUrl, truncate } from "@/lib/seo";

const REL_LABEL = { BORN_IN: "Born in", RAISED_IN: "Raised in", WORKED_IN: "Worked in", DIED_IN: "Died in" };
const initials = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

export default function PersonProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [article, setArticle] = useState(null);
  const [artState, setArtState] = useState("loading"); // loading | ready | pending
  const { lang } = useLanguage();
  const tts = useTTS();

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null); setErr(false); setArticle(null); setArtState("loading");
    getPerson(id).then(setP).catch(() => setErr(true));
  }, [id]);

  useEffect(() => {
    if (!p) return;
    getPersonArticle(id)
      .then((a) => { setArticle(a); setArtState("ready"); })
      .catch(() => setArtState("pending"));
  }, [p, id]);

  const readable = useMemo(() => {
    if (!article) return p ? [p.wiki_extract || p.wiki_desc || ""] : [];
    return [article.intro, ...(article.sections || []).map((s) => `${s.heading}. ${s.body}`)].filter(Boolean);
  }, [article, p]);
  useEffect(() => { if (p && tts) tts.setContent(readable); }, [p, readable, tts]);

  if (err) return <div className="mx-auto max-w-3xl px-4 py-24 text-center"><h1 className="font-display text-3xl text-navy"><T>Person not found</T></h1><Link to="/people" className="mt-4 inline-block text-oldglory-red"><T>Back to People</T></Link></div>;
  if (!p) return <div className="mx-auto max-w-[1100px] px-4 py-24"><div className="h-64 animate-pulse rounded-xl bg-parchment" /></div>;

  return (
    <div data-testid="person-profile">
      <Seo
        title={p.name}
        path={`/people/${id}`}
        type="profile"
        image={p.wiki_image || p.wiki_thumb}
        description={p.wiki_desc || p.wiki_extract || `${p.name} — an independent profile on USAPulse covering achievements, context and legacy, with a Wikipedia reference.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: p.name,
          description: truncate(p.wiki_desc || p.wiki_extract, 300) || undefined,
          image: p.wiki_image || p.wiki_thumb || undefined,
          url: canonicalUrl(`/people/${id}`),
          sameAs: p.wiki_url ? [p.wiki_url] : undefined,
          ...(p.president ? { jobTitle: "President of the United States" } : {}),
        }}
      />
      {/* Hero */}
      <div className="relative overflow-hidden bg-navy text-white">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-oldglory-red/20 blur-3xl" />
        <div className="relative mx-auto flex max-w-[1100px] flex-wrap items-center gap-6 px-4 py-12">
          {p.wiki_image || p.wiki_thumb ? (
            <img src={p.wiki_image || p.wiki_thumb} alt={p.name} className="h-28 w-28 rounded-2xl object-cover ring-2 ring-white/25" />
          ) : (
            <span className="flex h-28 w-28 items-center justify-center rounded-2xl bg-white/10 font-display text-3xl font-black ring-2 ring-white/20">{initials(p.name)}</span>
          )}
          <div className="min-w-0">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-oldglory-red"><T>{p.category}</T></span>
            <h1 className="font-display text-4xl font-black md:text-6xl">{p.name}</h1>
            {p.wiki_desc && <div className="mt-2 max-w-2xl text-white/75"><T>{p.wiki_desc}</T></div>}
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1100px] gap-10 px-4 py-10 lg:grid-cols-[1fr_320px]">
        {/* Main article */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold text-navy"><T>Profile</T></h2>
            {artState === "ready" && <InlineListen paragraphs={readable} testid="person-listen-btn" />}
          </div>

          {artState === "loading" && (
            <div className="flex items-center gap-3 rounded-xl border border-border bg-parchment/40 px-5 py-8 text-muted-foreground" data-testid="person-article-loading">
              <Loader2 className="h-5 w-5 animate-spin text-oldglory-red" /> <T>Preparing this profile…</T>
            </div>
          )}

          {artState === "pending" && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-6" data-testid="person-article-pending">
              <div className="flex items-center gap-2 font-semibold text-amber-800"><Sparkles className="h-5 w-5" /> <T>Full profile is being written</T></div>
              <p className="mt-1.5 text-sm text-amber-800/80"><T>Our AI is composing an original, neutral profile for this person. In the meantime, see the verified Wikipedia summary on the right. Please check back shortly.</T></p>
            </div>
          )}

          {artState === "ready" && article && (
            <div className="space-y-7" data-testid="person-article">
              {article.intro && <p className="text-lg leading-relaxed text-ink/85"><T>{article.intro}</T></p>}
              {(article.fields || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {article.fields.map((f, i) => <span key={i} className="rounded-full bg-navy/5 px-3 py-1 text-xs font-medium text-navy">{f}</span>)}
                </div>
              )}
              {(article.sections || []).map((s, i) => (
                <div key={i}>
                  <h3 className="mb-2 font-display text-xl font-bold text-navy"><T>{s.heading}</T></h3>
                  {s.body.split("\n").filter(Boolean).map((para, j) => (
                    <p key={j} className="mb-3 leading-relaxed text-ink/85"><T>{para}</T></p>
                  ))}
                </div>
              ))}

              {(article.quotes || []).length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-display text-xl font-bold text-navy"><T>In Their Words</T></h3>
                  {article.quotes.map((q, i) => (
                    <blockquote key={i} className="border-l-4 border-oldglory-red bg-parchment/40 py-2 pl-4 italic text-ink/85">
                      <Quote className="mb-1 h-4 w-4 text-oldglory-red" /> "{q.text}"
                      {q.source && <cite className="mt-1 block text-xs not-italic text-muted-foreground">— {q.source}</cite>}
                    </blockquote>
                  ))}
                </div>
              )}

              {(article.timeline || []).length > 0 && (
                <div>
                  <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-navy"><CalendarClock className="h-5 w-5 text-oldglory-red" /> <T>Timeline</T></h3>
                  <ol className="relative space-y-3 border-l-2 border-parchment pl-5">
                    {article.timeline.map((t, i) => (
                      <li key={i} className="relative">
                        <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full bg-oldglory-red ring-4 ring-white" />
                        <span className="font-display font-bold text-navy">{t.year}</span>
                        <span className="ml-2 text-ink/80">{t.event}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <p className="border-t border-border pt-4 text-[11px] text-muted-foreground">
                <T>Independently written by USAPulse AI from public sources. Verify important details with the references. Last reviewed</T> {new Date().getFullYear()}.
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          {/* Presidency facts (presidents only) */}
          {p.president && (
            <div className="rounded-xl border-2 border-navy/15 bg-white p-5" data-testid="presidency-card">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-oldglory-red">
                <Landmark className="h-4 w-4" /> <T>Presidency</T>
              </div>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground"><T>Number</T></dt><dd className="font-semibold text-navy">#{p.president_number}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground"><T>Term</T></dt><dd className="font-semibold text-navy">{p.term}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground"><T>Party</T></dt><dd className="font-semibold text-navy">{p.party}</dd></div>
                {p.vice_president && <div className="flex justify-between gap-3"><dt className="text-muted-foreground"><T>Vice President</T></dt><dd className="text-right font-semibold text-navy">{p.vice_president}</dd></div>}
              </dl>
              <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3 text-xs">
                {p.predecessor ? (
                  <Link to={`/people/${p.predecessor.id}`} className="flex items-center gap-1 font-semibold text-navy hover:text-oldglory-red" data-testid="pres-predecessor"><ArrowRight className="h-3.5 w-3.5 rotate-180" /> {p.predecessor.name}</Link>
                ) : <span className="text-muted-foreground"><T>First president</T></span>}
                {p.successor ? (
                  <Link to={`/people/${p.successor.id}`} className="flex items-center gap-1 font-semibold text-navy hover:text-oldglory-red" data-testid="pres-successor">{p.successor.name} <ArrowRight className="h-3.5 w-3.5" /></Link>
                ) : <span className="text-muted-foreground"><T>Most recent</T></span>}
              </div>
            </div>
          )}

          {/* Prominent Wikipedia reference card */}
          <div className="overflow-hidden rounded-xl border-2 border-navy/15 bg-white shadow-sm" data-testid="person-wikipedia-card">
            {(p.wiki_image || p.wiki_thumb) && (
              <img src={p.wiki_image || p.wiki_thumb} alt={p.name} className="h-48 w-full object-cover" />
            )}
            <div className="p-5">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-oldglory-red">
                <BookOpen className="h-4 w-4" /> <T>Wikipedia</T>
              </div>
              {p.wiki_desc && <div className="mt-1 font-display text-base font-bold text-navy">{p.wiki_desc}</div>}
              {p.wiki_extract && <p className="mt-2 line-clamp-6 text-sm leading-relaxed text-ink/75">{p.wiki_extract}</p>}
              <a
                href={p.wiki_url || p.wikipedia?.wikipedia_url}
                target="_blank" rel="noopener noreferrer"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-deep"
                data-testid="person-wikipedia-link"
              >
                <T>Read more on Wikipedia</T> <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <p className="mt-2 text-[10px] text-muted-foreground"><T>Summary & image via Wikipedia (CC BY-SA)</T></p>
            </div>
          </div>

          {/* Connected places */}
          {(p.relations || []).length > 0 && (
            <div className="rounded-xl border border-border bg-white p-5">
              <span className="mb-3 block text-xs font-bold uppercase tracking-widest text-navy"><T>Connected Places</T></span>
              <div className="space-y-2">
                {p.relations.map((r, i) => (
                  <Link key={i} to={`/place${r.url_path}`} className="group flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-parchment" data-testid={`person-place-${r.geo_id}`}>
                    <span>
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-oldglory-red"><T>{REL_LABEL[r.type] || r.type}</T></span>
                      <span className="flex items-center gap-1.5 font-medium text-ink"><MapPin className="h-3.5 w-3.5 text-navy" /> {r.name}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground/50 transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <Link to="/people" className="block text-center text-sm font-semibold text-oldglory-red hover:underline" data-testid="back-to-people"><T>← All Notable Americans</T></Link>
        </aside>
      </div>
    </div>
  );
}
